import {
  AutocompleteLocationUniversalFragment,
  LocationsForAutocompleteUniversalDocument,
  LocationsForAutocompleteUniversalQuery,
  LocationsForAutocompleteUniversalQueryVariables,
} from "@graphql/api";
import { appendMoreOptionsLink, googleSearch } from "@lib/services/find_locations";
import { getConfig } from "@lib/use_config";
import Fuse from "fuse.js";

type FixedMatches = {
  [state: string]: AutocompleteLocationUniversalFragment[];
};

function locationListByServiceArea(
  list: AutocompleteLocationUniversalFragment[],
  serviceArea: number | number[],
) {
  return list.filter((loc) => {
    if (typeof serviceArea === "number") {
      return loc.serviceAreas.includes(serviceArea);
    } else if (typeof serviceArea === "object") {
      return loc.serviceAreas.some((sa) => serviceArea.includes(sa));
    }
  });
}

async function hudsonSearch(
  searchTerm: string,
  serviceArea?: number | number[],
  limitLocations?: number,
) {
  const client = global.window.apolloClient;
  const { data }: { data: LocationsForAutocompleteUniversalQuery } = await client.query({
    query: LocationsForAutocompleteUniversalDocument,
  });

  let filteredLocationList = serviceArea
    ? locationListByServiceArea(data?.locationsForAutocomplete || [], serviceArea)
    : data?.locationsForAutocomplete || [];

  // Check if search term could be a location code (less than 5 chars and not purely numeric)
  const searchTermTrimmed = searchTerm.trim();
  const firstWord = searchTermTrimmed.split(/\s+/)[0];
  const isPotentialCode = firstWord.length < 5 && !/^\d+$/.test(firstWord);

  let keyedLocationList = filteredLocationList.map(
    (loc: AutocompleteLocationUniversalFragment) => ({
      ...loc,
      index: `${loc.fullState} ${loc.city} ${loc.displayName}`,
    }),
  );

  const fuseKeys: Fuse.FuseOptionKey[] = [
    { name: "index" },
    { name: "displayAddress", weight: 1 },
    { name: "city", weight: 2 },
    { name: "fullState", weight: 2 },
    { name: "metadata", weight: 1 },
  ];

  // Only include code in search if the search term could be a location code
  if (isPotentialCode) {
    fuseKeys.push({ name: "code", weight: 3 });
  }

  const fuse = new Fuse(keyedLocationList, {
    fieldNormWeight: 0.5,
    includeScore: true,
    useExtendedSearch: true,
    ignoreLocation: true,
    keys: fuseKeys,
    threshold: 0.3,
  });

  // Find matches as flat array
  let fixedMatches: Fuse.FuseResult<AutocompleteLocationUniversalFragment>[] = fuse.search(
    searchTerm.trim(),
  );

  // If search term is a potential code, prioritize exact code matches
  if (isPotentialCode) {
    const exactCodeMatch = fixedMatches.findIndex(
      (match) => match.item.code?.toLowerCase() === firstWord.toLowerCase()
    );
    if (exactCodeMatch > 0) {
      // Move exact code match to the front
      const [match] = fixedMatches.splice(exactCodeMatch, 1);
      fixedMatches.unshift(match);
    }
  }

  // Reduce number of matches to something reasonable
  fixedMatches = fixedMatches.slice(0, limitLocations ? limitLocations : 10);

  // See if we can get rid of low scoring matches
  const highScoring = fixedMatches.filter((loc) => loc.score && loc.score < 0.3);
  if (highScoring.length) fixedMatches = highScoring;

  // Organize matches by state
  let fixedMatchesByState: FixedMatches = {};
  fixedMatches.forEach(function (loc) {
    fixedMatchesByState[loc.item.fullState || ""] ||= [];
    fixedMatchesByState[loc.item.fullState || ""].push(loc.item);
  });

  // Convert matches to LocationPredictions
  let fixedLocationPredictions: be.LocationPrediction[] = [];
  for (const [state, locations] of Object.entries(fixedMatchesByState)) {
    fixedLocationPredictions.push({
      source: "title",
      title: state,
      key: state,
      type: "locality",
    });
    locations.forEach(function (loc) {
      if (loc.code) {
        fixedLocationPredictions.push({
          final: "false",
          farecode: loc.code,
          key: `${loc.code}.${loc.serviceAreas.join(",")}`,
          servicearea: loc.serviceAreas,
          source: "universalLocation",
          title: loc.displayName || "",
          description: `${loc.city}, ${loc.state}`,
          type: loc.type,
          typestring: loc.typeString || "generic",
        });
      }
    });
  }
  return fixedLocationPredictions;
}

export async function preloadLocations() {
  const client = global.window?.apolloClient;
  if (!client) return null;
  const queryVars: LocationsForAutocompleteUniversalQueryVariables = {};

  await client.query({
    query: LocationsForAutocompleteUniversalDocument,
    variables: queryVars,
  });
}

export async function findLocations({
  searchTerm,
  searchCenter,
  searchRadius,
  serviceArea,
  options,
}: be.FindLocationsFunctionArgs): Promise<be.LocationPrediction[]> {
  if (!searchTerm) return [];

  const { data } = await getConfig(["googleMapsDisabled"]);
  const googleDisabled = data?.config?.googleMapsDisabled;

  if (!options?.forceGoogle) {
    const hudsonResults = await hudsonSearch(searchTerm, serviceArea, options?.limitLocations);
    if (hudsonResults.length) {
      await appendMoreOptionsLink(hudsonResults);
      return hudsonResults;
    }
  }

  if (googleDisabled) return [];

  const googleResults: be.LocationPrediction[] = await googleSearch(
    searchTerm,
    searchCenter,
    searchRadius,
    options?.limitLocations,
  );
  if (googleResults.length) googleResults[0].first = "true";
  return googleResults.map((loc) => ({ ...loc, source: "universalGoogle" }));
}
